import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAdminAuth, type AdminClient, type Employee } from "@/hooks/useAdminAuth";
import { SERVICE_PLAN_FLAT_LABELS } from "@/pages/admin/clients/constants";
import ClientTable from "@/pages/admin/clients/components/ClientTable";
import ClientModal from "@/pages/admin/clients/components/ClientModal";
import ClientDetailModal from "@/pages/admin/clients/components/ClientDetailModal";
import ClientAssignModals from "@/pages/admin/clients/components/ClientAssignModals";

export default function AdminClientsPage() {
  const [urlParams] = useSearchParams();
  const {
    fetchClients, createClient, updateClient, deleteClient,
    fetchClientDetail, assignAdvisor, fetchEmployees, isSuperAdmin,
    hasClientSubPermission, fetchRecentFollowup,
  } = useAdminAuth();

  const [clients, setClients] = useState<AdminClient[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [countryFilter, setCountryFilter] = useState(urlParams.get("country") || "all");
  const [servicePlanFilter, setServicePlanFilter] = useState(urlParams.get("service_plan") || "all");
  const [isSingleFilter, setIsSingleFilter] = useState("all");
  const [progressStatusFilter, setProgressStatusFilter] = useState("all");
  const [advisorFilter, setAdvisorFilter] = useState("all");
  const [clientSourceFilter, setClientSourceFilter] = useState(urlParams.get("client_source") || "all");
  const [jointStimFilter, setJointStimFilter] = useState("all");
  const [hasGeneticFilter, setHasGeneticFilter] = useState("all");
  const [geneticTypeFilter, setGeneticTypeFilter] = useState("all");
  const [advisors, setAdvisors] = useState<Employee[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [followupSort, setFollowupSort] = useState<"asc" | "desc" | null>(null);
  const [followups, setFollowups] = useState<Record<number, string | null>>({});

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [editingClient, setEditingClient] = useState<AdminClient | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");

  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [detailClient, setDetailClient] = useState<AdminClient | null>(null);
  const [detailData, setDetailData] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [copiedField, setCopiedField] = useState<{ id: number; field: string } | null>(null);
  const [detailCopied, setDetailCopied] = useState<string | null>(null);

  // Batch
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showBatchAssignConfirm, setShowBatchAssignConfirm] = useState(false);
  const [showBatchDeleteModal, setShowBatchDeleteModal] = useState(false);
  const [showBatchAssignModal, setShowBatchAssignModal] = useState(false);
  const [batchAssignAdvisor, setBatchAssignAdvisor] = useState("");
  const [batchAssignLoading, setBatchAssignLoading] = useState(false);
  const [batchAssignError, setBatchAssignError] = useState("");
  const [batchAssignProgress, setBatchAssignProgress] = useState<{ current: number; total: number; failed: number } | null>(null);
  const [batchDeleteLoading, setBatchDeleteLoading] = useState(false);
  const [batchDeleteProgress, setBatchDeleteProgress] = useState<{ current: number; total: number; failed: number } | null>(null);

  // Single assign
  const [assignClient, setAssignClient] = useState<AdminClient | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedAdvisor, setSelectedAdvisor] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState("");

  const canAssign = hasClientSubPermission("assign_advisor");
  const canDelete = hasClientSubPermission("delete_client");

  const handleCopy = async (text: string, clientId: number, field: string) => {
    if (!text) return;
    try { await navigator.clipboard.writeText(text); setCopiedField({ id: clientId, field }); setTimeout(() => setCopiedField(null), 1500); }
    catch { const ta = document.createElement("textarea"); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); setCopiedField({ id: clientId, field }); setTimeout(() => setCopiedField(null), 1500); }
  };

  const handleDetailCopy = async (text: string, field: string) => {
    if (!text) return;
    try { await navigator.clipboard.writeText(text); setDetailCopied(field); setTimeout(() => setDetailCopied(null), 1500); }
    catch { const ta = document.createElement("textarea"); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); setDetailCopied(field); setTimeout(() => setDetailCopied(null), 1500); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    const isSuper = isSuperAdmin();
    const allowAdvisorFilter = isSuper || canAssign;
    const res = await fetchClients({
      search: search || undefined,
      country: countryFilter === "all" ? undefined : countryFilter,
      service_plan: servicePlanFilter === "all" ? undefined : servicePlanFilter,
      client_source: clientSourceFilter === "all" ? undefined : clientSourceFilter,
      is_single: isSingleFilter === "all" ? undefined : isSingleFilter,
      progress_status: progressStatusFilter === "all" ? undefined : progressStatusFilter,
      advisor_id: allowAdvisorFilter && advisorFilter !== "all" ? advisorFilter : undefined,
      has_genetic: hasGeneticFilter === "all" ? undefined : hasGeneticFilter,
      genetic_type: geneticTypeFilter === "all" ? undefined : geneticTypeFilter,
      limit: pageSize, offset: page * pageSize,
    });
    setLoading(false);
    if (res?.success) { setClients(res.clients || []); setCount(res.count || 0); }
  }, [fetchClients, search, countryFilter, servicePlanFilter, clientSourceFilter, isSingleFilter, progressStatusFilter, advisorFilter, hasGeneticFilter, geneticTypeFilter, page, pageSize, isSuperAdmin, canAssign]);

  useEffect(() => { load(); const loadF = async () => { const r = await fetchRecentFollowup(); if (r?.success) setFollowups(r.followups || {}); }; loadF(); }, [page, countryFilter, servicePlanFilter, clientSourceFilter, isSingleFilter, progressStatusFilter, advisorFilter, hasGeneticFilter, geneticTypeFilter]);

  useEffect(() => { if (isSuperAdmin() || canAssign) { fetchEmployees({ status: "active", limit: 100 }).then((r: any) => { if (r?.success) setAdvisors((r.employees || []).filter((e: Employee) => e.status === "active")); }); } }, [fetchEmployees, isSuperAdmin, canAssign]);

  const handleSearch = () => { setPage(0); load(); };

  const handleExportCSV = async () => {
    const isSuper = isSuperAdmin();
    const allowAdvisorFilter = isSuper || canAssign;
    const res = await fetchClients({
      search: search || undefined, country: countryFilter === "all" ? undefined : countryFilter,
      service_plan: servicePlanFilter === "all" ? undefined : servicePlanFilter,
      client_source: clientSourceFilter === "all" ? undefined : clientSourceFilter,
      is_single: isSingleFilter === "all" ? undefined : isSingleFilter,
      progress_status: progressStatusFilter === "all" ? undefined : progressStatusFilter,
      advisor_id: allowAdvisorFilter && advisorFilter !== "all" ? advisorFilter : undefined,
      limit: 99999, offset: 0,
    });
    if (!res?.success || !res.clients?.length) { alert("没有可导出的数据"); return; }
    const BOM = "\uFEFF";
    const headers = ["序号", "客户编码", "客户姓名", "服务方案", "国家", "专属顾问", "进度状态", "创建时间"];
    const lines = res.clients.map((c: AdminClient, idx: number) => {
      const name = c.is_single === false && c.partner_name ? `${c.name || "-"} & ${c.partner_name}` : (c.name || "-");
      return [String(idx + 1), ((c as any).client_code || "-"), name, SERVICE_PLAN_FLAT_LABELS[c.service_plan || ""] || "-", c.country || "-", c.advisor_employee_name || c.advisor_name || "-", c.current_stage_status === "in_progress" ? c.current_stage_name || "进行中" : c.current_stage_name === "-" || !c.current_stage_name ? "-" : "已结束", c.created_at].map((v: string) => `"${v.replace(/"/g, '""')}"`).join(",");
    });
    const csv = BOM + headers.join(",") + "\n" + lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `客户列表_${new Date().toISOString().slice(0, 10)}.csv`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const openCreate = () => { setModalMode("create"); setEditingClient(null); setModalError(""); setShowModal(true); };
  const openEdit = (client: AdminClient) => { setModalMode("edit"); setEditingClient(client); setModalError(""); setShowModal(true); };
  const openDetail = async (client: AdminClient) => { setDetailClient(client); setDetailLoading(true); const res = await fetchClientDetail(client.id); setDetailLoading(false); if (res?.success) { const pd = (res.progress || []).map((p: any) => ({ ...p, media: Array.isArray(p.media) ? p.media : [], documents: Array.isArray(p.documents) ? p.documents : [] })); setDetailData({ ...res, progress: pd }); } else setDetailData(res); };

  const openAssign = async (client: AdminClient) => { setAssignClient(client); setSelectedAdvisor(client.advisor_employee_id ? String(client.advisor_employee_id) : ""); setAssignError(""); const r = await fetchEmployees({ status: "active", limit: 100 }); if (r?.success) setEmployees((r.employees || []).filter((e: Employee) => e.status === "active")); };

  const handleSave = async (payload: Record<string, unknown>) => {
    setModalLoading(true); setModalError("");
    const res = modalMode === "create" ? await createClient(payload) : editingClient ? await updateClient(editingClient.id, payload) : null;
    setModalLoading(false);
    if (res?.success) { setShowModal(false); load(); }
    else setModalError(res?.error || "操作失败");
  };

  const handleDelete = async () => {
    if (deleteConfirm === null) return;
    const res = await deleteClient(deleteConfirm);
    if (res?.success) { setDeleteConfirm(null); load(); }
  };

  const handleAssign = async () => {
    if (!assignClient || !selectedAdvisor) return;
    setAssignLoading(true); setAssignError("");
    const r = await assignAdvisor(assignClient.id, Number(selectedAdvisor));
    setAssignLoading(false);
    if (r?.success) { setAssignClient(null); load(); }
    else setAssignError(r?.error || "分配失败");
  };

  const openBatchAssign = async () => {
    setShowBatchAssignModal(true); setBatchAssignAdvisor(""); setBatchAssignError(""); setBatchAssignProgress(null);
    const r = await fetchEmployees({ status: "active", limit: 100 });
    if (r?.success) setEmployees((r.employees || []).filter((e: Employee) => e.status === "active"));
  };

  const handleBatchAssign = async () => {
    if (!batchAssignAdvisor || selectedIds.size === 0) return;
    setBatchAssignLoading(true); setBatchAssignError(""); setBatchAssignProgress(null);
    const ids = [...selectedIds]; let failed = 0;
    for (let i = 0; i < ids.length; i++) {
      setBatchAssignProgress({ current: i + 1, total: ids.length, failed });
      const r = await assignAdvisor(ids[i], Number(batchAssignAdvisor));
      if (!r?.success) failed++;
      await new Promise((r) => setTimeout(r, 200));
    }
    setBatchAssignLoading(false);
    if (failed === 0) { setShowBatchAssignModal(false); setSelectedIds(new Set()); setBatchAssignAdvisor(""); load(); }
    else { setBatchAssignError(`${failed} 个客户分配失败，其余已成功分配`); setBatchAssignProgress(null); }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.size === 0) return;
    setBatchDeleteLoading(true); setBatchDeleteProgress(null);
    const ids = [...selectedIds]; let failed = 0;
    for (let i = 0; i < ids.length; i++) {
      setBatchDeleteProgress({ current: i + 1, total: ids.length, failed });
      const r = await deleteClient(ids[i]);
      if (!r?.success) failed++;
      await new Promise((r) => setTimeout(r, 200));
    }
    setBatchDeleteLoading(false); setShowBatchDeleteModal(false); setSelectedIds(new Set()); load();
  };

  return (
    <>
      <ClientTable
        clients={clients} count={count} loading={loading} page={page} pageSize={pageSize}
        search={search} countryFilter={countryFilter} servicePlanFilter={servicePlanFilter}
        clientSourceFilter={clientSourceFilter} isSingleFilter={isSingleFilter}
        progressStatusFilter={progressStatusFilter} advisorFilter={advisorFilter}
        jointStimFilter={jointStimFilter} hasGeneticFilter={hasGeneticFilter}
        geneticTypeFilter={geneticTypeFilter} followupSort={followupSort} followups={followups}
        advisors={advisors} canAssign={canAssign} canDelete={canDelete}
        isSuperAdmin={isSuperAdmin()} selectedIds={selectedIds}
        setSearch={setSearch} setCountryFilter={setCountryFilter}
        setServicePlanFilter={setServicePlanFilter} setClientSourceFilter={setClientSourceFilter}
        setIsSingleFilter={setIsSingleFilter} setProgressStatusFilter={setProgressStatusFilter}
        setAdvisorFilter={setAdvisorFilter} setJointStimFilter={setJointStimFilter}
        setHasGeneticFilter={setHasGeneticFilter} setGeneticTypeFilter={setGeneticTypeFilter}
        setPage={setPage} setPageSize={setPageSize} setFollowupSort={setFollowupSort}
        setSelectedIds={setSelectedIds}
        onSearch={handleSearch} onExportCSV={handleExportCSV}
        onCreate={openCreate} onDetail={openDetail} onEdit={openEdit}
        onAssign={openAssign} onDelete={setDeleteConfirm}
        onBatchAssign={() => setShowBatchAssignConfirm(true)}
        onBatchDelete={() => setShowBatchDeleteModal(true)}
        copiedField={copiedField} onCopy={handleCopy}
      />

      {showModal && (
        <ClientModal
          mode={modalMode} client={editingClient} loading={modalLoading}
          error={modalError} onClose={() => setShowModal(false)}
          onSave={handleSave} fetchClientDetail={fetchClientDetail}
        />
      )}

      {detailClient && (
        <ClientDetailModal
          client={detailClient} detailData={detailData} loading={detailLoading}
          isSuperAdmin={isSuperAdmin()} onClose={() => { setDetailClient(null); setDetailData(null); }}
          detailCopied={detailCopied} onCopy={handleDetailCopy}
        />
      )}

      <ClientAssignModals
        assignClient={assignClient} employees={employees}
        selectedAdvisor={selectedAdvisor} assignLoading={assignLoading} assignError={assignError}
        onAssignClose={() => setAssignClient(null)} onAdvisorChange={setSelectedAdvisor}
        onAssignConfirm={handleAssign}
        showBatchConfirm={showBatchAssignConfirm} selectedCount={selectedIds.size}
        onBatchConfirmClose={() => setShowBatchAssignConfirm(false)}
        onBatchConfirmProceed={() => { setShowBatchAssignConfirm(false); openBatchAssign(); }}
        showBatchModal={showBatchAssignModal} batchAdvisor={batchAssignAdvisor}
        batchLoading={batchAssignLoading} batchError={batchAssignError}
        batchProgress={batchAssignProgress}
        onBatchModalClose={() => { setShowBatchAssignModal(false); setBatchAssignProgress(null); }}
        onBatchAdvisorChange={setBatchAssignAdvisor} onBatchAssign={handleBatchAssign}
        deleteConfirm={deleteConfirm} onDeleteClose={() => setDeleteConfirm(null)}
        onDeleteConfirm={handleDelete}
        showBatchDelete={showBatchDeleteModal} batchDeleteLoading={batchDeleteLoading}
        batchDeleteProgress={batchDeleteProgress}
        onBatchDeleteClose={() => { setShowBatchDeleteModal(false); setBatchDeleteProgress(null); }}
        onBatchDeleteConfirm={handleBatchDelete}
      />
    </>
  );
}